import {
  Injectable,
  Inject,
  autobind,
  forwardRef,
} from '@kaokei/use-vue-service';
import { DaoService } from '../dao.service';

import { ExamService } from './exam.service';

@Injectable()
export class CountdownService {
  /**
   * 这里需要通过forwardRef解决循环引用的问题
   *
   * @type {ExamService}
   * @memberof CountdownService
   */
  @Inject(forwardRef(() => ExamService))
  public examService!: ExamService;

  public remainTime = 0; // 实际倒计时剩余时间，单位是秒
  public initialRemainTime = 0; // 初始倒计时剩余时间，单位是秒
  public START_TIME = 0; // 记录倒计时开始时刻，用于倒计时偏差

  public countdownTimer = 0; // 每隔1秒的倒计时定时器
  public COUNTDOWN_GAP = 1000; // 每隔1秒倒计时一次

  public serverSyncTimer = 0; // 每隔1分钟和服务器同步一次时间的定时器
  public SERVER_SYNC_GAP = 60 * 1000; // 和服务器同步的间隔时间

  private timeout = false; // 是否已经倒计时结束
  private timeoutCallback: any; // 倒计时结束的回调函数

  constructor(public daoService: DaoService) {}

  public registerCallback(callback: any) {
    this.timeoutCallback = callback;
  }

  public unregisterCallback() {
    this.timeoutCallback = undefined;
  }

  /**
   * 更新实际剩余时间
   * 利用本地时间实现倒计时纠偏
   * 大量考试，少数用户的本地时间有较大变动的时候，会导致自动提前交卷，去掉读取本地时间直接用服务端返回的时间做倒计时
   * @memberof CountdownService
   */
  public updateRemainTime() {
    this.START_TIME += 1000;
    this.remainTime = Math.round(
      (this.initialRemainTime - this.START_TIME) / 1000
    );
    if (this.remainTime < 0) {
      this.remainTime = 0;
    }
  }

  /**
   * 获取服务端剩余时间
   *
   * @return {*}
   * @memberof CountdownService
   */
  public getRemainTime() {
    return this.daoService
      .getRemainTime(this.examService.userExamId)
      .then(res => {
        this.START_TIME = 0;
        this.initialRemainTime = res.remainTime;
      });
  }

  /**
   * 业务需要调用的唯一入口
   *
   * @return {*}
   * @memberof CountdownService
   */
  public startCountdown() {
    return this.getRemainTime().then(() => {
      this.updateRemainTime();
      if (this.remainTime > 0) {
        this.setCountdownTimer();
        this.setServerSyncTimer();
      }
    });
  }

  /**
   * 设置倒计时的定时器
   *
   * @memberof CountdownService
   */
  public setCountdownTimer() {
    window.clearTimeout(this.countdownTimer);
    this.countdownTimer = window.setTimeout(this.countdown, this.COUNTDOWN_GAP);
  }

  /**
   * 设置服务器时间同步的定时器
   *
   * @memberof CountdownService
   */
  public setServerSyncTimer() {
    window.clearTimeout(this.serverSyncTimer);
    this.serverSyncTimer = window.setTimeout(
      this.serverSyncTime,
      this.SERVER_SYNC_GAP
    );
  }

  /**
   * 倒计时的定时器的处理函数
   *
   * @memberof CountdownService
   */
  @autobind
  public countdown() {
    this.updateRemainTime();
    if (this.remainTime > 0) {
      this.countdownTimer = window.setTimeout(
        this.countdown,
        this.COUNTDOWN_GAP
      );
    } else {
      this.onTimeout();
    }
  }

  /**
   * 同步服务器时间的处理函数
   *
   * @memberof CountdownService
   */
  @autobind
  public serverSyncTime() {
    this.getRemainTime().then(() => {
      this.updateRemainTime();
      if (this.remainTime > 0) {
        this.serverSyncTimer = window.setTimeout(
          this.serverSyncTime,
          this.SERVER_SYNC_GAP
        );
      } else {
        this.onTimeout();
      }
    });
  }

  /**
   * 触发超时回调，且只会触发一次
   *
   * @return {*}
   * @memberof CountdownService
   */
  public onTimeout() {
    if (this.timeout) {
      return;
    }
    this.timeout = true;
    if (this.timeoutCallback) {
      this.timeoutCallback();
    }
    this.dispose();
  }

  public dispose() {
    this.timeout = true;
    this.unregisterCallback();
    window.clearTimeout(this.countdownTimer);
    window.clearTimeout(this.serverSyncTimer);
  }
}
